import { MaterialCatalogRecord, useMaterialCatalogCache } from "api/cached";
import { OptionItem } from "commons/ultilities/dynamic-form/models/control";
import { mapToDistinctOptionItems } from "commons/ultilities/option-item/option-item-ultil";
import { useEffect, useMemo } from "react";

export type MaterialGroupSelectionKey = "code" | "barcode" | "type" | "name";

type UseMaterialGroupSelectionProps = {
  codeSelected: string | null;
  typeSelected: string | null;
  nameSelected: string | null;
};

const filterByKeyword = (options: OptionItem[], keyword: string) => {
  if (!keyword) return options;
  return options.filter((x) =>
    x.label.toLowerCase().includes(keyword.toLowerCase()),
  );
};

const mapDistinctTextFieldOptions = <T>(
  items: T[],
  selector: (item: T) => string | null | undefined,
): OptionItem[] => {
  return mapToDistinctOptionItems(
    items.filter((item) => !!selector(item)),
    selector,
  );
};

export const useMaterialGroupSelection = ({
  codeSelected,
  typeSelected,
  nameSelected,
}: UseMaterialGroupSelectionProps) => {
  const { data, error } = useMaterialCatalogCache();

  const materialFilterOptions = useMemo<MaterialCatalogRecord[]>(() => {
    return data ?? [];
  }, [data]);

  useEffect(() => {
    if (!error) return;
    console.error("Fail load material filter data", error);
  }, [error]);

  const searchCodeOptions = async (keyword: string) => {
    const filteredMaterial = materialFilterOptions.filter((item) => {
      const matchesType = !typeSelected || item.type === typeSelected;
      const matchesName = !nameSelected || item.name === nameSelected;
      return matchesType && matchesName;
    });
    const codeOptions = mapDistinctTextFieldOptions(
      filteredMaterial,
      (item) => item.code,
    );
    return filterByKeyword(codeOptions, keyword);
  };

  const searchTypeOptions = async (keyword: string) => {
    const filteredMaterial = materialFilterOptions.filter((item) => {
      const matchesCode = !codeSelected || item.code === codeSelected;
      const matchesName = !nameSelected || item.name === nameSelected;
      return matchesCode && matchesName;
    });
    const typeOptions = mapDistinctTextFieldOptions(
      filteredMaterial,
      (item) => item.type,
    );
    return filterByKeyword(typeOptions, keyword);
  };

  const searchNameOptions = async (keyword: string) => {
    const filteredMaterial = materialFilterOptions.filter((item) => {
      const matchesCode = !codeSelected || item.code === codeSelected;
      const matchesType = !typeSelected || item.type === typeSelected;
      return matchesCode && matchesType;
    });
    const nameOptions = mapDistinctTextFieldOptions(
      filteredMaterial,
      (item) => item.name,
    );
    return filterByKeyword(nameOptions, keyword);
  };

  return {
    searchCodeOptions,
    searchTypeOptions,
    searchNameOptions,
  };
};
